import { get, post } from '@/utils/queries';
import { useEffect, useMemo, useState } from 'react';
import { User } from '@types';
import { Spinner } from '@/components/Spinner';
import type { FileItem } from '@types';
import GenericConfirmationModal from '@/components/Modal/GenericConfirmationModal';
import useFileData from '@/hooks/useFileData';
import {
  getCheckedFileIdRecursive,
  setFilesIsChecked,
  transformFileItemtoFileItemExtended,
} from '@/components/UsersAccordionList/EditPermissionsModalSteps/utils';
import { FileItemExtended } from '@/components/UsersAccordionList/EditPermissionsModalSteps/interfaces';
import { alertErrorMessage, alertMessageSuccessWithLink } from '@/utils/alerts';

interface Props {
  clientId: number;
  thirdPartyId: number;
  isOpen: boolean;
  fileItem: FileItem;
  onClose: () => void;
}

export default function AcceptPermission({
  clientId,
  thirdPartyId,
  isOpen,
  fileItem,
  onClose,
}: Props) {
  const [filesState, setFilesState] = useState<FileItemExtended | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
  const { getFileData, refreshFileData } = useFileData();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data: userData, isValidating: isLoading } = get(
    `users/list/${clientId}`
  );
  const {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    data: rootFolder,
    isValidating: isLoadingFileData,
  } = getFileData(clientId);

  useEffect(() => {
    if (!isLoadingFileData) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const clientFolders: FileItem = rootFolder?.files[0];
      setFilesState(
        clientFolders
          ? transformFileItemtoFileItemExtended(clientFolders, thirdPartyId)
          : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, isLoadingFileData]);

  const thirdPartyUser = useMemo(() => {
    if (userData) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      return (userData as User[]).find((u) => u.id === thirdPartyId);
    }
    return null;
  }, [thirdPartyId, userData]);

  const onSave = async () => {
    setIsSaving(true);
    if (
      submitAttempted ||
      (fileItem.permissions &&
        fileItem.permissions.length > 0 &&
        fileItem.permissions[0].allowedUsers.includes(thirdPartyId))
    ) {
      setSubmitAttempted(true);
      alertErrorMessage('The user already has access to this file.');
    } else {
      setSubmitAttempted(true);
      const result = setFilesIsChecked(
        filesState as FileItemExtended,
        fileItem.id,
        true
      );
      const listSelectedFolders: string[] = [];
      getCheckedFileIdRecursive(result, listSelectedFolders);

      try {
        await post(`permissions/files`, {
          filesIds: listSelectedFolders,
          clientId,
          userIds: [thirdPartyId],
        });
        alertMessageSuccessWithLink(
          'Permission granted. To review permissions click ',
          'here',
          `${window.location.origin}/account/${clientId}/${thirdPartyId}?edit-permissions-modal=open&client-user=true`
        );
        await refreshFileData(clientId);
      } catch (err) {
        alertErrorMessage('Something went wrong. Please try again.');
      }
    }
    setIsSaving(false);
    onClose();
  };

  if (isLoading || isLoadingFileData) {
    return <Spinner />;
  }

  return (
    <GenericConfirmationModal
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onAcceptAction={() => onSave()}
      onCloseAction={() => onClose()}
      showModal={isOpen}
      isLoading={isSaving}
      modalMessage={`Are you sure you want to allow ${
        thirdPartyUser?.first_name as string
      } ${thirdPartyUser?.last_name as string} 
        to access ${fileItem.name}?`}
    />
  );
}
